/**
 * 売上まわりの操作ログ表示調整（精算状態変更などで BE が複数バッチ／冗長 changes を返す場合の FE 側の見せ方）。
 */

import type { OperationLogBatchSummaryType, OperationLogChangeEntry } from '@/models/operation-log'

/** 「売上を更新」グループの group_label（詳細 API） */
export const OPERATION_LOG_SALES_UPDATE_GROUP_LABEL = '売上を更新'

/** 「〇〇の入金を登録|変更|削除」 */
const SALES_PAYMENT_ITEM_LABEL = /^.+の入金を(登録|変更|削除)$/

/** 「科目名 を更新」（補助科目など） */
const SALES_SUBJECT_SPACE_UPDATE_LABEL = /^.+\s+を更新$/
/** 「科目名 を追加」→ 表示上「科目名の詳細登録」に寄せる */
const SALES_SUBJECT_SPACE_ADD_LABEL = /^(.+)\s+を追加$/

function summaryTargetType(sj: OperationLogBatchSummaryType['summary_json']): string | null {
  if (!sj || typeof sj !== 'object') return null
  const raw = (sj as { target_type?: unknown }).target_type
  return typeof raw === 'string' ? raw : null
}

function summaryAction(sj: OperationLogBatchSummaryType['summary_json']): string | null {
  if (!sj || typeof sj !== 'object') return null
  const raw = (sj as { action?: unknown }).action
  return typeof raw === 'string' ? raw : null
}

function summaryTargetNames(sj: OperationLogBatchSummaryType['summary_json']): string[] {
  if (!sj || typeof sj !== 'object') return []
  const raw = (sj as { target_names?: unknown }).target_names
  if (!Array.isArray(raw)) return []
  return raw.map(v => (typeof v === 'string' ? v : String(v)))
}

/** 一覧日時 "2026/04/22 09:28:57" を ms に */
export function parseOperationLogListDatetime(datetime: string | null | undefined): number | null {
  if (datetime == null) return null
  const s = String(datetime).trim()
  const m = /^(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/.exec(s)
  if (!m) return null
  const [, y, mo, d, h, mi, se] = m
  const t = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(se)).getTime()
  return Number.isNaN(t) ? null : t
}

/** 同一予約・売上更新で「売上」だけの要約行が、具体科目の行とペアのときは一覧から省く */
export function filterRedundantGenericSalesSummaryRows(
  list: OperationLogBatchSummaryType[],
  opts: { genericSalesLabels: readonly string[]; timeWindowMs?: number },
): OperationLogBatchSummaryType[] {
  const { genericSalesLabels, timeWindowMs = 20_000 } = opts
  const genericSet = new Set(genericSalesLabels.filter(s => s.trim() !== ''))

  const isSalesFeature = (item: OperationLogBatchSummaryType) =>
    String(item.feature_slug ?? item.module ?? '')
      .trim()
      .toLowerCase() === 'sales'

  const hasSpecificSibling = (item: OperationLogBatchSummaryType, index: number, t0: number): boolean => {
    const rid = item.reservation_id
    if (rid == null || String(rid).trim() === '') return false

    for (let j = 0; j < list.length; j++) {
      if (j === index) continue
      const other = list[j]
      if (other.reservation_id !== rid) continue
      if (!isSalesFeature(other)) continue
      if (summaryTargetType(other.summary_json) !== 'sales') continue
      if (summaryAction(other.summary_json) !== 'update') continue

      const names = summaryTargetNames(other.summary_json)
      const isSpecific = names.length > 1 || (names.length === 1 && !genericSet.has(names[0]))
      if (!isSpecific) continue

      const t1 = parseOperationLogListDatetime(other.datetime)
      if (t1 == null) continue
      if (Math.abs(t1 - t0) <= timeWindowMs) return true
    }
    return false
  }

  return list.filter((item, index) => {
    if (!isSalesFeature(item)) return true
    if (summaryTargetType(item.summary_json) !== 'sales') return true
    if (summaryAction(item.summary_json) !== 'update') return true

    const names = summaryTargetNames(item.summary_json)
    if (names.length !== 1) return true
    const only = names[0]
    if (!genericSet.has(only)) return true

    const t0 = parseOperationLogListDatetime(item.datetime)
    if (t0 == null) return true

    return !hasSpecificSibling(item, index, t0)
  })
}

type SalesGroupedItemInput = {
  id?: number
  item_label?: string
  changes?: OperationLogChangeEntry[]
}

function cloneGroupedItems(items: SalesGroupedItemInput[] | undefined): SalesGroupedItemInput[] {
  return (items ?? []).map(it => ({
    ...it,
    changes: [...(it.changes ?? [])],
  }))
}

function normalizeSalesGroupedItemLabel(itemLabel: string | undefined): string | undefined {
  const label = String(itemLabel ?? '').trim()
  if (label === '') return itemLabel
  const m = SALES_SUBJECT_SPACE_ADD_LABEL.exec(label)
  if (m?.[1]) return `${m[1]}の詳細登録`
  return itemLabel
}

/**
 * 「売上を更新」グループ内で、入金日時を入金ブロックへ寄せ、ノイズになりやすい行を畳む。
 */
export function mergeSalesUpdateGroupedItems(items: SalesGroupedItemInput[]): SalesGroupedItemInput[] {
  const working = cloneGroupedItems(items).map(it => ({
    ...it,
    item_label: normalizeSalesGroupedItemLabel(it.item_label),
  }))

  const paymentIdx = working.findIndex(it => SALES_PAYMENT_ITEM_LABEL.test(String(it.item_label ?? '').trim()))
  const saleIdx = working.findIndex(it => String(it.item_label ?? '').trim() === '売上更新')

  if (paymentIdx !== -1 && saleIdx !== -1) {
    const pay = working[paymentIdx]
    const sale = working[saleIdx]
    const saleChanges = [...(sale.changes ?? [])]
    const move: typeof saleChanges = []
    const rest: typeof saleChanges = []

    for (const c of saleChanges) {
      const f = String(c.field ?? '').trim()
      if (f === 'payment_at_unixtime') move.push(c)
      else if (f === 'primary_sales_subject_name') continue
      else rest.push(c)
    }

    pay.changes = [...(pay.changes ?? []), ...move]
    sale.changes = rest
  }

  return working.filter(it => {
    const label = String(it.item_label ?? '').trim()
    if (label === '売上更新' && (it.changes?.length ?? 0) === 0) return false
    if (SALES_SUBJECT_SPACE_UPDATE_LABEL.test(label)) {
      const ch = it.changes ?? []
      if (ch.length === 1 && String(ch[0].field ?? '').trim() === 'sales_sub_subject_id') return false
    }
    return true
  })
}

